import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useDataOptional } from '../../contexts/DataContext';
import { useAiChat } from '../../hooks/useAiChat';
import SidebarItemMenu from '../common/SidebarItemMenu';
import '../common/SidebarItemMenu.css';
import './ChatHistoryPanel.css';
import '../primitives/Button.css';
const botHead = '/scout_images/Scout_Thumbsup.png';

const PIN_STORAGE_KEY = 'chatHistory.pinnedIds';

export default function ChatHistoryPanel({
  onConversationSelected,
  activeConversationId,
  suppressAutoSelectFirst = false,
  onNewChat,
  variant = 'default',
  mode = 'expanded',
  style,
  onToggleCollapse,
  sidebarCollapsed,
  externalSearchQuery
}) {
  const { user, isInitialized } = useAuth();
  const {
    loadExistingConversations,
    loadConversationMessages,
    deleteConversation
  } = useAiChat('all');
  const dataCtx = useDataOptional() || {};
  const coursesById = useMemo(() => dataCtx.state?.coursesById || {}, [dataCtx.state?.coursesById]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('chatSidebarCollapsed')) || false;
    } catch { return false; }
  });
  const [searchQuery, setSearchQuery] = useState('');
  const effectiveSearchQuery = externalSearchQuery != null ? externalSearchQuery : searchQuery;
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('chatHistoryCollapsed')) || { courses:false, documents:false, chats:false };
    } catch { return { courses:false, documents:false, chats:false }; }
  });
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState(null); // { conversationId, title }
  const [pinnedIds, setPinnedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PIN_STORAGE_KEY)) || []; } catch { return []; }
  });

  const togglePin = useCallback((conversationId) => {
    setPinnedIds(prev => {
      const next = prev.includes(conversationId)
        ? prev.filter(id => id !== conversationId)
        : [...prev, conversationId];
      try { localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);



  const saveCollapsed = useCallback((next) => {
    setCollapsed(next);
    try { localStorage.setItem('chatHistoryCollapsed', JSON.stringify(next)); } catch {}
  }, []);

  // Save sidebar collapse state
  useEffect(() => {
    try {
      localStorage.setItem('chatSidebarCollapsed', JSON.stringify(isCollapsed));
    } catch {}
  }, [isCollapsed]);

  // Resolve a display name for a conversation — for course chats, use the actual course name
  const getDisplayTitle = useCallback((c) => {
    const raw = c.title || '';
    const isCourse = raw.trim().toLowerCase().startsWith('course:') ||
      (Array.isArray(c.attachedEndeavorIds) && c.attachedEndeavorIds.length > 0);

    // For course chats, always try resolving the real name from DataContext first
    if (isCourse && Array.isArray(c.attachedEndeavorIds) && c.attachedEndeavorIds.length > 0) {
      for (const eid of c.attachedEndeavorIds) {
        const course = coursesById[eid] || coursesById[String(eid)];
        if (course) {
          return course.name || course.title || course.endeavorName || raw.replace(/^course:\s*/i, '') || 'Untitled Chat';
        }
      }
    }
    // Fallback: strip Course: prefix and return the remainder
    if (raw.trim().toLowerCase().startsWith('course:')) {
      const stripped = raw.replace(/^course:\s*/i, '').trim();
      // If the stripped result is generic ("Course", "Unknown Course", empty), try harder
      if (!stripped || stripped.toLowerCase() === 'course' || stripped.toLowerCase() === 'unknown course') {
        // Try matching by course ID from all courses
        const allCourses = Object.values(coursesById);
        if (allCourses.length === 1) return allCourses[0].name || allCourses[0].title || stripped;
      }
      return stripped || 'Untitled Chat';
    }
    // If it's a course chat but no Course: prefix, resolve from DataContext
    if (isCourse) {
      return raw || 'Untitled Chat';
    }
    // For document chats, strip the prefix
    if (raw.trim().toLowerCase().startsWith('document:')) {
      return raw.replace(/^document:\s*/i, '');
    }
    return raw || 'Untitled Chat';
  }, [coursesById]);

  const getType = useCallback((c) => {
    // Check attachments first (more reliable)
    if (Array.isArray(c.attachedEndeavorIds) && c.attachedEndeavorIds.length > 0) return 'course';
    // Only single-document chats belong in the Documents section
    if (Array.isArray(c.attachedDocumentIds) && c.attachedDocumentIds.length === 1) return 'document';
    
    // Fallback to title prefix
    const t = (c.title || '').trim().toLowerCase();
    if (t.startsWith('course:')) return 'course';
    if (t.startsWith('document:') || t.startsWith('doc:')) return 'document';
    return 'other';
  }, []);

  const handleSelect = useCallback(async (c) => {
    if (!c) return;
    // If already loaded, just select
    if (c.messages && Array.isArray(c.messages) && c.messages.length > 0) {
      console.log('[ChatHistoryPanel] Conversation already has messages in memory, using cached', { conversationId: c.conversationId, messageCount: c.messages.length });
      onConversationSelected?.(c);
      return;
    }
    // Lazy load messages now
    try {
      console.log('[ChatHistoryPanel] Loading messages from backend for', c.conversationId);
      const m = await loadConversationMessages(c.conversationId, { page: 0, size: 20 });
      const updated = { ...c, messages: m.messages, messagePagination: m.pagination };
      setConversations(prev => prev.map(p => p.conversationId === c.conversationId ? updated : p));
      onConversationSelected?.(updated);
    } catch (e) {
      console.warn('ChatHistoryPanel: load messages failed', e);
      onConversationSelected?.(c);
    }
  }, [loadConversationMessages, onConversationSelected]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const hydrate = useCallback(async () => {
    if (!user || !isInitialized) return;
    setLoading(true);
    try {
      const list = await loadExistingConversations(user.id || user.userId);
      const bare = list.map(c => {
        const hasEndeavorIds = Array.isArray(c.attachedEndeavorIds) && c.attachedEndeavorIds.length > 0;
        const titleLooksCourse = (c.title || '').toLowerCase().startsWith('course: ');
        return {
          ...c,
          conversationId: c.id,
          messages: null,
          messagePagination: null,
          attachedDocumentIds: Array.isArray(c.attachedDocumentIds) ? c.attachedDocumentIds : [],
          attachedEndeavorIds: Array.isArray(c.attachedEndeavorIds) ? c.attachedEndeavorIds : [],
          isCourseChat: titleLooksCourse || hasEndeavorIds,
          type: getType(c)
        };
      });
      bare.sort((a,b)=> new Date(b.updatedAt)-new Date(a.updatedAt));
      setConversations(bare);
    } finally {
      setLoading(false);
    }
  }, [user, isInitialized, loadExistingConversations, getType]); // intentional deps; avoids flicker

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  // Auto-select first conversation once loaded (separate effect removes deps from hydrate)
  useEffect(() => {
    if (suppressAutoSelectFirst) return; // skip auto-select to allow clean new chat state
    if (!loading && conversations.length && !activeConversationId) {
      handleSelect(conversations[0]);
    }
  }, [loading, conversations, activeConversationId, handleSelect, suppressAutoSelectFirst]);

  const handleDelete = (e, c) => { // eslint-disable-line no-unused-vars
    e.stopPropagation();
    setDeleteConfirmation({
      conversationId: c.conversationId,
      title: c.title || 'Untitled Chat'
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmation) return;
    
    const ok = await deleteConversation(deleteConfirmation.conversationId);
    if (ok) {
      setConversations(prev => prev.filter(p => p.conversationId !== deleteConfirmation.conversationId));
      if (activeConversationId === deleteConfirmation.conversationId) {
        onConversationSelected?.(null);
      }
    }
    setDeleteConfirmation(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation(null);
  };

  // Close modal on escape key
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && deleteConfirmation) {
        handleCancelDelete();
      }
    };

    if (deleteConfirmation) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [deleteConfirmation]);



  const handleRename = useCallback((conversationId, currentTitle) => {
    setRenaming(conversationId);
    setRenameValue(currentTitle || 'Untitled Chat');
  }, []);

  const handleRenameSubmit = useCallback(async (conversationId) => {
    if (!renameValue.trim()) return;
    
    try {
      // Update the conversation title in state
      setConversations(prev => prev.map(c => 
        c.conversationId === conversationId 
          ? { ...c, title: renameValue.trim() }
          : c
      ));
      setRenaming(null);
      setRenameValue('');
      // Note: You might want to add an API call here to persist the rename
    } catch (error) {
      console.error('Failed to rename conversation:', error);
      alert('Failed to rename conversation');
    }
  }, [renameValue]);

  const handleRenameCancel = useCallback(() => {
    setRenaming(null);
    setRenameValue('');
  }, []);



  // NEW: helper to add/merge a single conversation object (e.g., after creation elsewhere)
  const upsertConversation = useCallback((conv) => {
    if (!conv) return;
    console.log('[ChatHistoryPanel upsertConversation]', {
      conversationId: conv.conversationId || conv.id,
      title: conv.title,
      hasMessages: !!conv.messages,
      messageCount: conv.messages?.length || 0,
      isActiveConversation: activeConversationId === (conv.conversationId || conv.id)
    });
    setConversations(prev => {
      const exists = prev.some(p => p.conversationId === (conv.conversationId || conv.id));
      if (exists) {
        const updated = prev.map(p => {
          if (p.conversationId === (conv.conversationId || conv.id)) {
            // Merge with existing, preserving properties from conv
            return { ...p, ...conv, conversationId: conv.conversationId || conv.id };
          }
          return p;
        });
        
        // If this is the active conversation and it's being updated with messages,
        // notify parent to update its state (but only if messages changed)
        if (activeConversationId === (conv.conversationId || conv.id) && conv.messages) {
          const existing = prev.find(p => p.conversationId === (conv.conversationId || conv.id));
          const messagesChanged = !existing?.messages || existing.messages.length !== conv.messages.length;
          if (messagesChanged) {
            // Call onConversationSelected to sync the messages back to parent
            setTimeout(() => {
              const merged = updated.find(p => p.conversationId === (conv.conversationId || conv.id));
              if (merged) onConversationSelected?.(merged);
            }, 0);
          }
        }
        
        return updated;
      }
      const mapped = {
        ...conv,
        conversationId: conv.conversationId || conv.id,
        messages: conv.messages || null,
        messagePagination: conv.messagePagination || null,
        attachedDocumentIds: Array.isArray(conv.attachedDocumentIds) ? conv.attachedDocumentIds : [],
        attachedEndeavorIds: Array.isArray(conv.attachedEndeavorIds) ? conv.attachedEndeavorIds : [],
        isCourseChat: (conv.title || '').toLowerCase().startsWith('course: ') || (Array.isArray(conv.attachedEndeavorIds) && conv.attachedEndeavorIds.length > 0),
        type: getType(conv)
      };
      const next = [mapped, ...prev];
      // maintain updatedAt ordering
      next.sort((a,b)=> new Date(b.updatedAt)-new Date(a.updatedAt));
      return next;
    });
  }, [getType, activeConversationId, onConversationSelected]);

  // When activeConversationId changes and we do not yet have it, attempt to fetch it individually
  useEffect(() => {
    if (!activeConversationId || !user || !isInitialized) return;
    const hasIt = conversations.some(c => c.conversationId === activeConversationId);
    if (hasIt) return;
    let abort = false;
    (async () => {
      try {
        // Fetch full list (API lacks single-get) then pick the one we need
        const list = await loadExistingConversations(user.id || user.userId);
        const found = (list || []).find(c => c.id === activeConversationId || c.conversationId === activeConversationId);
        if (found && !abort) {
          upsertConversation(found);
        }
      } catch (e) {
        console.warn('ChatHistoryPanel: failed to fetch missing active conversation', e);
      }
    })();
    return () => { abort = true; };
  }, [activeConversationId, user, isInitialized, conversations, loadExistingConversations, upsertConversation]);

  // After first prompt & AI response, titles may auto-update server side; provide a refresh API
  const refreshConversationTitle = useCallback(async (conversationId) => {
    if (!conversationId || !user) return;
    try {
      const list = await loadExistingConversations(user.id || user.userId);
      const found = list.find(c => c.id === conversationId || c.conversationId === conversationId);
      if (found) {
        // IMPORTANT: The list endpoint may include a lightweight messages array for the newest conversation.
        // Passing those here causes AiChatCard to treat them as incremental updates and MERGE them with
        // the locally streamed first user + ai messages, resulting in duplicates. We intentionally strip
        // any messages/messagePagination from this refresh so that only the title/metadata updates.
        const sanitized = { ...found };
        delete sanitized.messages;
        delete sanitized.messagePagination;
        upsertConversation(sanitized);

        if (activeConversationId === (found.conversationId || found.id)) {
          // Preserve any existing in-memory messages for the active conversation to avoid flicker/duplication
          const existing = conversations.find(c => c.conversationId === conversationId);
            onConversationSelected?.({
              ...(existing ? existing : {}),
              ...sanitized,
              conversationId: sanitized.conversationId || sanitized.id,
              messages: existing?.messages || null,
              messagePagination: existing?.messagePagination || null,
              __metaOnly: true
            });
        }
      }
    } catch (e) {
      console.warn('ChatHistoryPanel: refreshConversationTitle failed', e);
    }
  }, [user, loadExistingConversations, upsertConversation, activeConversationId, onConversationSelected, conversations]);

  // Expose refresh via window for now (could be lifted via prop drilling if needed)
  useEffect(() => {
    window.__chatHistoryRefreshTitle = refreshConversationTitle;
    return () => { if (window.__chatHistoryRefreshTitle === refreshConversationTitle) delete window.__chatHistoryRefreshTitle; };
  }, [refreshConversationTitle]);

  // Expose upsert so external creators (AiChatCard lazy creation) can immediately add new chats to history
  useEffect(() => {
    window.__chatHistoryUpsert = upsertConversation;
    return () => { if (window.__chatHistoryUpsert === upsertConversation) delete window.__chatHistoryUpsert; };
  }, [upsertConversation]);

  const grouped = useMemo(() => {
    const byType = { course: [], document: [], other: [] };
    for (const c of conversations) {
      byType[c.type || getType(c)].push(c);
    }
    // Keep newest first inside each group
    Object.values(byType).forEach(arr => arr.sort((a,b)=> new Date(b.updatedAt)-new Date(a.updatedAt)));
    return byType;
  }, [conversations, getType]);

  const Section = ({ k, title, items }) => {
    let filteredItems = items.filter(c => 
      !effectiveSearchQuery || (c.title || 'Untitled Chat').toLowerCase().includes(effectiveSearchQuery.toLowerCase())
    );

    // Sort pinned items to top
    filteredItems = [...filteredItems].sort((a, b) => {
      const ap = pinnedIds.includes(a.conversationId) ? 0 : 1;
      const bp = pinnedIds.includes(b.conversationId) ? 0 : 1;
      return ap - bp;
    });

    if (filteredItems.length === 0 && searchQuery) return null;
    if (items.length === 0) return null;

    return (
      <div className="sidebar-section">
        <div
          className={`sidebar-section-header ${collapsed[k] ? 'collapsed' : ''}`}
          onClick={() => saveCollapsed({ ...collapsed, [k]: !collapsed[k] })}
        >
          {!isCollapsed && (
            <>
              <span className="sidebar-section-icon-inline">
                {k === 'courses' ? (
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2">
                    <path d="M2 4L8 2L14 4V12L8 14L2 12V4Z"/>
                    <path d="M8 2V14"/>
                    <path d="M2 4L8 6L14 4"/>
                  </svg>
                ) : k === 'documents' ? (
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2">
                    <path d="M4 2h5l4 4v8H4V2z"/>
                    <path d="M9 2v4h4"/>
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2">
                    <path d="M2 3C2 2.44772 2.44772 2 3 2H13C13.5523 2 14 2.44772 14 3V11C14 11.5523 13.5523 12 13 12H5L2 14V3Z"/>
                  </svg>
                )}
              </span>
              <span className="sidebar-section-title">{title}</span>
              <span className={`sidebar-section-caret ${collapsed[k] ? 'collapsed' : ''}`}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                  <path d="M4 3L7 6L4 9" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                </svg>
              </span>
            </>
          )}
          {isCollapsed && (
            <div className="sidebar-section-icon" title={title}>
              {k === 'courses' ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M2 4L8 2L14 4V12L8 14L2 12V4Z" stroke="currentColor" strokeWidth="1" fill="none"/>
                  <path d="M8 2V14" stroke="currentColor" strokeWidth="1"/>
                  <path d="M2 4L8 6L14 4" stroke="currentColor" strokeWidth="1"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M2 3C2 2.44772 2.44772 2 3 2H13C13.5523 2 14 2.44772 14 3V11C14 11.5523 13.5523 12 13 12H5L2 14V3Z" stroke="currentColor" strokeWidth="1" fill="none"/>
                </svg>
              )}
            </div>
          )}
        </div>
        <div className={`sidebar-section-list ${collapsed[k] ? 'collapsed' : ''}`}>
          {filteredItems.length === 0 && <div className="sidebar-empty">No chats</div>}
          {filteredItems.map(c => (
            <div
              key={c.conversationId}
              className={`sidebar-item ${c.conversationId === activeConversationId ? 'sidebar-item--active' : ''} ${pinnedIds.includes(c.conversationId) ? 'sidebar-item--pinned' : ''}`}
              onClick={() => { if (renaming !== c.conversationId) handleSelect(c); }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(c);
                }
              }}
            >
              {!isCollapsed ? (
                <>
                  <div className="sidebar-item-left">
                    {pinnedIds.includes(c.conversationId) && (
                      <span className="sidebar-item-pin-icon">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 17v5"/><path d="M9 2h6l-1 7h4l-5 8h-2l-5-8h4Z"/></svg>
                      </span>
                    )}
                    <span className="sidebar-item-title">
                      {renaming === c.conversationId ? (
                        <input
                          className="sim-rename-input"
                          type="text"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onBlur={() => handleRenameSubmit(c.conversationId)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleRenameSubmit(c.conversationId);
                            } else if (e.key === 'Escape') {
                              handleRenameCancel();
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                      ) : (
                        getDisplayTitle(c)
                      )}
                    </span>
                  </div>

                  <SidebarItemMenu
                    onRename={() => handleRename(c.conversationId, c.title)}
                    onPin={() => togglePin(c.conversationId)}
                    isPinned={pinnedIds.includes(c.conversationId)}
                    onDelete={() => {
                      setDeleteConfirmation({
                        conversationId: c.conversationId,
                        title: c.title || 'Untitled Chat'
                      });
                    }}
                  />
                </>
              ) : (
                <div className="sidebar-item-left" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };



  return (
    <div className={`chat-history-panel sidebar-container ${isCollapsed ? 'sidebar-container--collapsed' : ''}`} style={style}>
      {/* Top Header - ChatGPT Style */}
      <div className="sidebar-header sidebar-top">
        {isCollapsed ? (
          // COLLAPSED: only DocuHounds logo button
          <button
            className="sidebar-logo-button"
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label="Open DocuHounds chat history"
          >
            <img src={botHead} alt="DocuHounds" />
          </button>
        ) : variant === 'aitools' ? (
          // AI TOOLS PAGE: collapse button, search, and + button all on one line
          <div className="sidebar-header-content sidebar-header-content--aitools">
            <div className="aitools-header-row">
              <button
                type="button"
                className="sidebar-collapse-btn sidebar-collapse-button"
                onClick={onToggleCollapse}
                title="Collapse sidebar"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="sidebar-collapse-button-icon">
                  <path d="M9 4l-3 4 3 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 4l-3 4 3 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              
              <div className="sidebar-search aitools-search">
                <div className="sidebar-search-container">
                  <svg className="sidebar-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M7.5 13C10.8137 13 13.5 10.3137 13.5 7C13.5 3.68629 10.8137 1 7.5 1C4.18629 1 1.5 3.68629 1.5 7C1.5 10.3137 4.18629 13 7.5 13Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                    <path d="M13.5 13.5L11.5 11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <input
                    type="text"
                    className="sidebar-search-input"
                    placeholder="Search chats"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              
              <button
                type="button"
                className="sidebar-new-chat-btn-icon"
                onClick={() => {
                  console.log('[ChatHistoryPanel] New chat button clicked');
                  onNewChat?.();
                }}
                title="New chat"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        ) : variant === 'scoutIqDashboard' && mode === 'collapsed' ? (
          // SCOUTIQ COLLAPSED: only show search, no header controls
          <div className="sidebar-header-content sidebar-header-content--scoutiq-collapsed">
            {/* Search Bar only - no header controls in ScoutIQ collapsed mode */}
            <div className="sidebar-search">
              <div className="sidebar-search-container">
                <svg className="sidebar-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M7.5 13C10.8137 13 13.5 10.3137 13.5 7C13.5 3.68629 10.8137 1 7.5 1C4.18629 1 1.5 3.68629 1.5 7C1.5 10.3137 4.18629 13 7.5 13Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <path d="M13.5 13.5L11.5 11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                  type="text"
                  className="sidebar-search-input"
                  placeholder="Search chats"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>
        ) : (
          // EXPANDED: collapse chevron on top, search below
          <div className="sidebar-header-content">
            {/* Header controls - collapse and new chat buttons */}
            <div className="sidebar-header-controls">
              <button
                type="button"
                className="sidebar-collapse-btn sidebar-collapse-button"
                onClick={() => setIsCollapsed(!isCollapsed)}
                title="Collapse sidebar"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="sidebar-collapse-button-icon">
                  <path d="M9 4l-3 4 3 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 4l-3 4 3 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              
              <button
                type="button"
                className="sidebar-new-chat-btn-icon"
                onClick={() => {
                  console.log('[ChatHistoryPanel] New chat button clicked');
                  onNewChat?.();
                }}
                title="New chat"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {/* Search Bar - full width below collapse button */}
            <div className="sidebar-search">
              <div className="sidebar-search-container">
                <svg className="sidebar-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M7.5 13C10.8137 13 13.5 10.3137 13.5 7C13.5 3.68629 10.8137 1 7.5 1C4.18629 1 1.5 3.68629 1.5 7C1.5 10.3137 4.18629 13 7.5 13Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <path d="M13.5 13.5L11.5 11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                  type="text"
                  className="sidebar-search-input"
                  placeholder="Search chats"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Scrollable list area (courses + chats) */}
      <div className="sidebar-scroll-area">
        <div className="sidebar-content">
          {loading && <div className="sidebar-loading">Loading...</div>}
          {!loading && conversations.length === 0 && (
            <div className="sidebar-empty">
              <div className="sidebar-empty__badge sidebar-empty__badge--chat">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              </div>
              <h4 className="sidebar-empty__title">Start Your First Chat</h4>
              <p className="sidebar-empty__body">Ask Scout about your course docs, class notes, or assignments and get grounded answers fast.</p>
              <p className="sidebar-empty__hint">Tip: connect docs first for better answers</p>
            </div>
          )}
          {!loading && conversations.length > 0 && (
            <div className="sidebar-sections">
              <Section k="courses" title="Courses" items={grouped.course} />
              <Section k="documents" title="Documents" items={grouped.document} />
              <Section k="chats" title="Chats" items={grouped.other} />
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirmation && (
        <div className="delete-modal-overlay" onClick={handleCancelDelete}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="delete-modal-title">Delete chat?</h3>
            <p className="delete-modal-message">
              This will delete{' '}
              <span className="delete-modal-chat-name">"{deleteConfirmation.title}"</span>
            </p>
            <div className="delete-modal-buttons">
              <button
                className="delete-modal-button delete-modal-button--cancel"
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
              <button
                className="delete-modal-button delete-modal-button--delete"
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}